import { ReactComponent as PencilIcon } from '@basqet-dashboard/common/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@basqet-dashboard/common/assets/icons/trash.svg';
import { ReactComponent as WarningStaticIcon } from '@basqet-dashboard/common/assets/icons/warning-static.svg';
import { Button, Dropdown, FormInput, Modal } from '@basqet-dashboard/common/components';
import { COUNTRY_OPTIONS } from '@basqet-dashboard/common/config/countries';
import { InfoLabel, SidePanel } from '@components/onboarding';
import { ANALYTICS_EVENTS, KYB_ACCOUNT_TYPE } from '@config';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAnalytics, useTrackOnMount } from '@hooks';
import { useCompliance } from '@hooks/useCompliance';
import { mapPepDeclaration } from '@utils/compliance-mappers';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

import {
  PEP_POSITION_OPTIONS,
  PEP_RELATIONSHIP_OPTIONS,
  POSITION_STATUS_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS
} from '../corporate-non-fi-constants';
import { pepEntrySchema } from './pep-declaration.schema';

// ─── Icons ───────────────────────────────────────────────────────────────────

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isPepComplete = (pep) => {
  const requiredFilled =
    pep.full_name &&
    pep.position &&
    pep.relationship &&
    pep.position_held &&
    pep.position_status &&
    pep.country &&
    pep.source_of_funds;
  if (!requiredFilled) return false;
  if (pep.relationship === 'others' && !pep.specify_relationship) return false;
  return true;
};

// ─── PEP card ─────────────────────────────────────────────────────────────────

const PepCard = ({ pep, onEdit, onDelete, isIncomplete, readOnly }) => {
  const positionLabel =
    PEP_POSITION_OPTIONS.find((o) => o.value === pep.position)?.label || pep.position;
  const cardClassName = classNames('pep-declaration__card', {
    'pep-declaration__card--incomplete': isIncomplete
  });

  return (
    <div className={cardClassName}>
      <span className="pep-declaration__card-name">
        {pep.full_name}
        {positionLabel && (
          <span className="pep-declaration__card-position"> ({positionLabel})</span>
        )}
        {isIncomplete && <span className="pep-declaration__card-warning">Details required</span>}
      </span>
      {!readOnly && (
        <div className="pep-declaration__card-actions">
          <button
            type="button"
            className="pep-declaration__icon-btn"
            onClick={onEdit}
            aria-label="Edit PEP">
            <PencilIcon />
          </button>
          <button
            type="button"
            className="pep-declaration__icon-btn pep-declaration__icon-btn--danger"
            onClick={onDelete}
            aria-label="Delete PEP">
            <TrashIcon />
          </button>
        </div>
      )}
    </div>
  );
};

PepCard.propTypes = {
  pep: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isIncomplete: PropTypes.bool,
  readOnly: PropTypes.bool
};

// ─── PEP side panel ───────────────────────────────────────────────────────────

const PepPanel = ({ entry, onSave, onClose }) => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(pepEntrySchema),
    defaultValues: entry
      ? {
          full_name: entry.full_name || '',
          position: entry.position || '',
          relationship: entry.relationship || '',
          specify_relationship: entry.specify_relationship || '',
          position_held: entry.position_held || '',
          position_status: entry.position_status || '',
          country: entry.country || '',
          source_of_funds: entry.source_of_funds || ''
        }
      : {}
  });

  const watchedRelationship = useWatch({ control, name: 'relationship' });

  return (
    <SidePanel title="PEP" cursiveTitle="Details" onClose={onClose}>
      <div className="side-panel__body">
        <form id="pep-panel-form" onSubmit={handleSubmit(onSave)}>
          <div className="side-panel__fields">
            {/* Row 1: Full Name | Position */}
            <div className="side-panel__row">
              <FormInput
                label="Full Name of PEP"
                name="full_name"
                placeholder="First name, Last name"
                register={register('full_name')}
                error={errors.full_name?.message}
              />
              <Dropdown
                name="position"
                formLabel="Position of PEP"
                placeholder="Select position"
                options={PEP_POSITION_OPTIONS}
                register={register('position')}
                defaultValue={entry?.position}
                error={errors.position?.message}
                onSelect={(v) => setValue('position', v, { shouldValidate: true })}
              />
            </div>

            {/* Row 2: Relationship | Specify Relationship (conditional) */}
            <div className="side-panel__row">
              <Dropdown
                name="relationship"
                formLabel="Relationship to Govt. Official"
                placeholder="Select an option"
                options={PEP_RELATIONSHIP_OPTIONS}
                register={register('relationship')}
                defaultValue={entry?.relationship}
                error={errors.relationship?.message}
                onSelect={(v) => setValue('relationship', v, { shouldValidate: true })}
              />
              {watchedRelationship === 'others' ? (
                <FormInput
                  label="Specify Relationship"
                  name="specify_relationship"
                  placeholder="Describe your relationship"
                  register={register('specify_relationship')}
                  error={errors.specify_relationship?.message}
                />
              ) : (
                <div />
              )}
            </div>

            {/* Row 3: Position/Office Held | Position Status */}
            <div className="side-panel__row">
              <FormInput
                label="Position/Office Held"
                name="position_held"
                placeholder="e.g Minister, Commissioner, Military officer"
                register={register('position_held')}
                error={errors.position_held?.message}
              />
              <Dropdown
                name="position_status"
                formLabel="Is this Position Current or Former?"
                placeholder="Select an option"
                options={POSITION_STATUS_OPTIONS}
                register={register('position_status')}
                defaultValue={entry?.position_status}
                error={errors.position_status?.message}
                onSelect={(v) => setValue('position_status', v, { shouldValidate: true })}
              />
            </div>

            {/* Row 4: Country | Source of Funds */}
            <div className="side-panel__row">
              <Dropdown
                name="country"
                formLabel="Country Where Position was Held"
                placeholder="Select country"
                options={COUNTRY_OPTIONS}
                register={register('country')}
                defaultValue={entry?.country}
                error={errors.country?.message}
                onSelect={(v) => setValue('country', v, { shouldValidate: true })}
              />
              <Dropdown
                name="source_of_funds"
                formLabel={
                  <InfoLabel
                    text="Source of Funds"
                    tooltip="Provide the source of funds for the person you are declaring as a PEP or government official."
                  />
                }
                placeholder="Select an option"
                options={SOURCE_OF_FUNDS_OPTIONS}
                register={register('source_of_funds')}
                defaultValue={entry?.source_of_funds}
                error={errors.source_of_funds?.message}
                onSelect={(v) => setValue('source_of_funds', v, { shouldValidate: true })}
              />
            </div>
          </div>
        </form>
      </div>
      <div className="side-panel__footer">
        <Button isFull type="submit" form="pep-panel-form">
          Save and Continue
        </Button>
      </div>
    </SidePanel>
  );
};

PepPanel.propTypes = {
  entry: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

// ─── Main component ───────────────────────────────────────────────────────────

const PepDeclaration = ({ onComplete, pepPersons, initialData, onSaveDraft, readOnly = false }) => {
  const { track } = useAnalytics();
  const { saveSection, deleteEntry } = useCompliance();

  useTrackOnMount(ANALYTICS_EVENTS.KYB_PEP_VIEWED, {
    kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI
  });

  const [peps, setPeps] = useState(() => {
    const saved = initialData?.peps || [];
    const persons = pepPersons || [];

    if (persons.length === 0) return saved;

    const usedIds = new Set();
    return persons.map((p, i) => {
      const existing = saved.find((s) => s.full_name === p.name && !usedIds.has(s.id));
      if (existing) {
        usedIds.add(existing.id);
        return existing;
      }
      return {
        id: Date.now() + i,
        full_name: p.name,
        position: p.type,
        relationship: '',
        specify_relationship: '',
        position_held: '',
        position_status: '',
        country: '',
        source_of_funds: ''
      };
    });
  });

  const [pepEntryIds, setPepEntryIds] = useState(initialData?._pepEntryIds || {});
  const [noPepsConfirmed, setNoPepsConfirmed] = useState(initialData?.pep_confirmed_none || false);
  const [panel, setPanel] = useState(null); // { entry: object | null }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync newly added PEP persons from ownership changes into the peps list.
  // The component stays mounted (hidden) when navigating between steps, so
  // useState init only runs once — this effect handles subsequent changes.
  useEffect(() => {
    const persons = pepPersons || [];
    if (persons.length === 0) return;

    const createEntry = (p, i) => ({
      id: Date.now() + i,
      full_name: p.name,
      position: p.type,
      relationship: '',
      specify_relationship: '',
      position_held: '',
      position_status: '',
      country: '',
      source_of_funds: ''
    });

    setPeps((currentPeps) => {
      // Match on full_name only — the pep's position field gets overwritten
      // when the user fills the PEP details form, so it won't match p.type
      const newPersons = persons.filter(
        (p) => !currentPeps.some((pep) => pep.full_name === p.name)
      );
      if (newPersons.length === 0) return currentPeps;
      return [...currentPeps, ...newPersons.map(createEntry)];
    });

    setNoPepsConfirmed(false);
  }, [pepPersons]);

  const allPepsComplete = peps.length > 0 && peps.every(isPepComplete);
  const canSubmit = allPepsComplete || noPepsConfirmed;

  const openPanel = (entry = null) => setPanel({ entry });
  const closePanel = () => setPanel(null);

  const handlePanelSave = (data) => {
    let newPeps;
    if (panel.entry) {
      newPeps = peps.map((p) => (p.id === panel.entry.id ? { ...p, ...data } : p));
    } else {
      track(ANALYTICS_EVENTS.KYB_PEP_ENTRY_ADDED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
        pep_name: data.full_name
      });
      newPeps = [...peps, { id: Date.now(), ...data }];
    }
    setPeps(newPeps);
    onSaveDraft?.({ peps: newPeps, no_peps_confirmed: noPepsConfirmed });
    closePanel();
  };

  const handleDeletePep = async () => {
    try {
      const entryId = pepEntryIds[deleteTarget];
      if (entryId) {
        await deleteEntry('PEP_DECLARATION', entryId);
      }

      track(ANALYTICS_EVENTS.KYB_PEP_ENTRY_REMOVED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI
      });

      const newPeps = peps.filter((p) => p.id !== deleteTarget);
      setPeps(newPeps);
      onSaveDraft?.({ peps: newPeps, no_peps_confirmed: noPepsConfirmed });
      setPepEntryIds((prev) => {
        const next = { ...prev };
        delete next[deleteTarget];
        return next;
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete PEP:', error);
      toast.error('Failed to delete PEP entry');
    }

    setDeleteTarget(null);
    setShowDeleteModal(false);
  };

  const handleNoPepsChange = (e) => {
    const checked = e.target.checked;
    setNoPepsConfirmed(checked);
    if (checked) {
      // Clear all PEP entries when confirming none
      setPeps([]);
    }
    onSaveDraft?.({ peps: checked ? [] : peps, no_peps_confirmed: checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (peps.length > 0 && !peps.every(isPepComplete)) {
      toast.error('Please complete all PEP details before continuing.');
      return;
    }

    try {
      setIsSubmitting(true);

      track(ANALYTICS_EVENTS.KYB_PEP_SAVED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI
      });

      if (noPepsConfirmed) {
        // Delete any existing PEP entries before saving "no PEPs" confirmation
        for (const entryId of Object.values(pepEntryIds)) {
          await deleteEntry('PEP_DECLARATION', entryId);
        }
        // Save single "no PEPs" entry
        const apiData = mapPepDeclaration({ pep_confirmed_none: true });
        await saveSection('PEP_DECLARATION', apiData);
        setPepEntryIds({});
      } else {
        // Save all PEP entries in parallel
        const results = await Promise.all(
          peps.map((pep, i) => {
            const apiData = mapPepDeclaration(pep);
            return saveSection('PEP_DECLARATION', apiData, {
              entryId: pepEntryIds[pep.id],
              position: i
            });
          })
        );
        results.forEach((result, i) => {
          if (result.entry_id) {
            setPepEntryIds((prev) => ({ ...prev, [peps[i].id]: result.entry_id }));
          }
        });
      }

      toast.success('PEP Declaration saved successfully');
      onComplete({ peps, pep_confirmed_none: noPepsConfirmed });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save PEP declaration:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pep-declaration">
      {!readOnly && (
        <button type="button" className="corp-step-form__add-btn" onClick={() => openPanel()}>
          + Add PEP
        </button>
      )}

      {peps.length > 0 && (
        <div className="pep-declaration__list">
          {peps.map((pep) => (
            <PepCard
              key={pep.id}
              pep={pep}
              isIncomplete={!isPepComplete(pep)}
              readOnly={readOnly}
              onEdit={() => openPanel(pep)}
              onDelete={() => {
                setDeleteTarget(pep.id);
                setShowDeleteModal(true);
              }}
            />
          ))}
        </div>
      )}

      {!readOnly && (
        <form onSubmit={handleSubmit} className="pep-declaration__form">
          {peps.length === 0 && (
            <label className="pep-declaration__checkbox-label">
              <input
                type="checkbox"
                className="pep-declaration__checkbox"
                checked={noPepsConfirmed}
                onChange={handleNoPepsChange}
              />
              By checking this box, I confirm that I don&apos;t have any Politically Exposed Persons
              (PEPs) in my organization.
            </label>
          )}
          <Button type="submit" isFull isLoading={isSubmitting} disabled={!canSubmit}>
            Save and Continue
          </Button>
        </form>
      )}

      {panel !== null && (
        <PepPanel entry={panel.entry} onSave={handlePanelSave} onClose={closePanel} />
      )}

      {showDeleteModal && (
        <Modal
          Icon={WarningStaticIcon}
          header={{ name: 'Delete', cursive: 'PEP Entry' }}
          onModalClose={() => {
            setShowDeleteModal(false);
            setDeleteTarget(null);
          }}>
          <p className="modal__text">Are you sure you want to delete this PEP entry?</p>
          <Button
            isFull
            variant="outline"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteTarget(null);
            }}>
            Cancel
          </Button>
          <Button isFull onClick={handleDeletePep}>
            Yes, continue
          </Button>
        </Modal>
      )}
    </div>
  );
};

PepDeclaration.propTypes = {
  onComplete: PropTypes.func.isRequired,
  pepPersons: PropTypes.array,
  initialData: PropTypes.object,
  onSaveDraft: PropTypes.func,
  readOnly: PropTypes.bool
};

export { PepDeclaration };
